import React from "react";
import { centerCrop, Crop, defaultCrop, makeAspectCrop, convertToPixelCrop } from "react-image-crop";
import { useQueryClient } from "react-query";
import { useCopies } from "shared/hooks/useCopies";
import { updateDashboardWithProfilePicture, useUploadImage } from "./dataloader";
import { UploadPictureStateType } from "./type";
import { getCanvas, uploadPictureInitialState } from "./utils";

export default function useUploadPictureService(
  handleClose: Function,
  regParticipantId?: string,
  uiVersion?: number,
  onSuccess?: Function
) {
  const copies = useCopies();
  const [state, setState] = React.useState<UploadPictureStateType>(uploadPictureInitialState);
  const [cropState, setCropState] = React.useState<Crop>(defaultCrop);
  const { mutateAsync: upload } = useUploadImage(regParticipantId, uiVersion);
  const queryClient = useQueryClient();

  const handleSelectImage = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0];
        const pattern = /image\/jpe?g|png/;

        if (!file.type.match(pattern)) {
          setState({
            ...state,
            error: copies.get("invalid.format")
          });
          return;
        }

        const reader = new FileReader();
        reader.addEventListener(
          "load",
          function () {
            setState((state: any) => ({
              ...state,
              error: "",
              imageSrc: reader?.result?.toString() || "",
              cropImage: true,
              cameraError: false
            }));
          },
          false
        );
        reader.readAsDataURL(file);
      }
    },
    [copies, state]
  );

  const centerImageOnLoad = React.useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { naturalWidth: nwidth, naturalHeight: nheight, width, height } = e.currentTarget;
      const crop = centerCrop(
        makeAspectCrop(
          {
            unit: "%",
            width: 90
          },
          1,
          nwidth,
          nheight
        ),
        nwidth,
        nheight
      );
      setCropState(convertToPixelCrop(crop, width, height));
      setState({ ...state, cropImageRef: e.currentTarget });
    },
    [state]
  );

  const onCropComplete = React.useCallback((crop: Crop) => {
    setCropState(crop);
  }, []);

  const getCroppedImage = React.useCallback(() => {
    const canvas = getCanvas(cropState, state.cropImageRef);
    canvas &&
      canvas.toBlob(async blob => {
        if (state.croppedImageUrl) {
          URL.revokeObjectURL(state.croppedImageUrl);
        }
        if (blob) {
          const formData = new FormData();
          formData.append("file", blob);
          const uploadResponse = await upload(formData);
          setState({
            ...state,
            croppedImageUrl: URL.createObjectURL(blob),
            cropImage: false,
            captureImage: false,
            imageSrc: uploadResponse.profilePictureLocation,
            cameraError: false
          });
          updateDashboardWithProfilePicture(queryClient, uploadResponse);
          onSuccess?.(uploadResponse);
        }
      }, "image/jpeg");
  }, [cropState, queryClient, state, upload, onSuccess]);

  const handleUpload = React.useCallback(() => {
    getCroppedImage();
  }, [getCroppedImage]);

  const handleUseCamera = React.useCallback(() => {
    setState({ ...state, error: "", cameraError: false, captureImage: true });
  }, [state]);

  const setCapturedImage = React.useCallback(
    (image: string) => {
      setState({ ...state, error: "", imageSrc: image, cropImage: true, captureImage: false });
    },
    [state]
  );

  const handleCameraError = React.useCallback(() => {
    setState({
      ...state,
      error: "",
      captureImage: false,
      cameraError: !state.browserSupportError
    });
  }, [state]);

  const handleUploadCancel = React.useCallback(() => {
    setState({ ...state, imageSrc: "", cropImage: false, captureImage: false });
  }, [state]);

  const handleBroswerSupport = React.useCallback(() => {
    setState({
      ...state,
      error: "",
      captureImage: false,
      browserSupportError: true
    });
  }, [state]);

  return {
    state,
    handleSelectImage,
    cropState,
    setCropState,
    handleUpload,
    centerImageOnLoad,
    onCropComplete,
    setCapturedImage,
    handleCameraError,
    handleUploadCancel,
    handleBroswerSupport,
    handleUseCamera
  };
}
